// Message priority (Mattermost post priority, `metadata.priority`) — the composer's one-shot
// choice and the label a received post carries. Port of the Apple app's pieces:
// ChannelViewModel.messagePriority ("important" | "urgent" | nil — one-shot: SENDING clears it,
// exactly like Android's composer), MacComposerControls' Estándar/Importante/Urgente picker, the
// "Se enviará como …" line above the Mac's field, and MessagePriorityPill (urgent = mention red,
// important = warning amber, standard draws nothing).
//
// The Mac has no "request acknowledgement" (requested_ack) and no persistent notifications, so
// neither does this client: the wire carries the level only.

export type Level = 'important' | 'urgent';

/** The picker's rows in the Mac's order; `null` is Estándar. */
export const choices: ReadonlyArray<Level | null> = [null, 'important', 'urgent'];

/** A stored/wire value → a level this client draws, or null (standard / unknown). */
export function level(value: string | null | undefined): Level | null {
  return value === 'important' || value === 'urgent' ? value : null;
}

/** The picker row's string key. */
export function choiceKey(choice: Level | null): string {
  return choice === null ? 'channel.priority.standard' : `channel.priority.${choice}`;
}

/** The line above the box while a priority is set (MacConversationView.composerFieldIdle). */
export function willSendKey(choice: Level): string {
  return `channel.priority.willSend.${choice}`;
}

/** The pill on a received post, or null when it draws nothing. */
export function pillKey(value: string | null | undefined): string | null {
  const l = level(value);
  return l ? `channel.priority.${l}` : null;
}

/** What rides `SendExtras.priority` — nothing at all for a standard message. */
export function wire(choice: Level | null): { priority: Level } | null {
  return choice ? { priority: choice } : null;
}

/**
 * Whether the composer offers the picker. The Mac always does; Mattermost's own client hides
 * it when the server turns `PostPriority` off (the server would drop the metadata anyway), so
 * the web follows the server when it says so — and only an explicit "false" hides it: an old
 * server that does not send the key keeps the Mac's behaviour.
 */
export function isAvailable(clientConfig: Readonly<Record<string, string>>): boolean {
  return clientConfig.PostPriority !== 'false';
}

export const MessagePriority = { choices, level, choiceKey, willSendKey, pillKey, wire, isAvailable } as const;
